import { useTranslation } from "react-i18next";
import { GitBranch, Lightbulb } from "lucide-react";
import { PanelSelect } from "../../../PanelSelect";
import { LEG_KINDS, TRAVELER_SUGGESTION } from "../../circuitLegs";
import type { WiringBasisProps } from "./types";

export const WiringBasis = ({ basis, boardName, legs }: WiringBasisProps) => {
  const { t } = useTranslation();

  const line = (key: string, params?: Record<string, string | number>) =>
    t(`smartSurveyor.cablesBeta.${key}`, params);
  const isSwitched = basis.units.length > 0;
  const hasWechsel = basis.units.some((unit) => unit.via !== null);
  const legKinds = legs ? LEG_KINDS.filter((kind) => legs.metres[kind]) : [];
  // Asked once, where it applies: answered either way, the leg carries a type of its own and it goes.
  const isTravelerOpen = Boolean(
    legs && hasWechsel && legs.metres.traveler && !legs.legTypes.traveler,
  );

  return (
    <div
      data-testid="wiring-basis"
      className="mb-2.5 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-2 text-[13px] leading-snug text-slate-700"
    >
      <div className="mb-1 flex items-center gap-1.5 font-semibold text-slate-800">
        <GitBranch size={14} className="flex-none text-slate-600" aria-hidden />
        {line("wiringTitle")}
      </div>
      <div className="mb-1.5 text-slate-600">
        {line(isSwitched ? "wiringSwitchedWhy" : "wiringDirectWhy")}
      </div>
      <div className="space-y-1">
        {basis.units.map((unit, index) => (
          <div key={index}>
            <span className="font-medium">
              {[boardName, unit.via, unit.control].filter(Boolean).join(" → ")}
              {unit.lights > 0 &&
                ` → ${line("wiringLights", { count: unit.lights })}`}
            </span>
            {unit.loads.length > 0 && (
              <span className="text-slate-600">
                {" · "}
                {line("wiringLoadsSeparate", { names: unit.loads.join(", ") })}
              </span>
            )}
          </div>
        ))}
        {basis.directCount > 0 && (
          <div>
            {line("wiringDirect", {
              count: basis.directCount,
              board: boardName,
            })}
          </div>
        )}
      </div>

      {legs && isSwitched && legKinds.length > 1 && (
        <div
          data-testid="wiring-legs"
          className="mt-2 space-y-2.5 border-t border-slate-200 pt-2"
        >
          {legKinds.map((kind) => {
            const own = legs.legTypes[kind];
            const value = own || legs.circuitType;
            const options = [
              ...new Set([legs.circuitType, ...legs.options].filter(Boolean)),
            ];
            return (
              // The leg's name on a line of its own: cut to "Zuleitung: Verteil…" it said nothing.
              <div key={kind}>
                <div className="font-medium text-slate-800">
                  {line(`wiringLeg_${kind}`)}
                </div>
                <div className="mt-0.5 flex items-center gap-2">
                  <span className="flex-none tabular-nums text-slate-600">
                    {legs.formatMetres(legs.metres[kind]?.totalM ?? 0)} m
                  </span>
                  <span className="min-w-0 flex-1">
                    <PanelSelect
                      value={value}
                      options={options.map((type) => ({
                        value: type,
                        label: type,
                      }))}
                      placeholder={t("smartSurveyor.cablesBeta.cableTypePick")}
                      ariaLabel={line(`wiringLeg_${kind}`)}
                      isEdited={Boolean(own) && own !== legs.circuitType}
                      onPick={(type) =>
                        legs.onPick(
                          kind,
                          type === legs.circuitType ? null : type,
                        )
                      }
                      onCreate={(type) => legs.onCreate(kind, type)}
                      createLabel={(text) =>
                        line("cableTypesCreate", { name: text })
                      }
                    />
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {legs && isTravelerOpen && (
        <div
          data-testid="wiring-traveler-suggestion"
          className="mt-2 rounded-md border border-blue-200 bg-white px-2.5 py-2"
        >
          <div className="flex items-start gap-1.5 text-slate-800">
            <Lightbulb
              size={14}
              className="mt-0.5 flex-none text-blue-600"
              aria-hidden
            />
            {line("wiringTravelerSuggest", { type: TRAVELER_SUGGESTION })}
          </div>
          <div className="mt-1.5 flex gap-1.5">
            <button
              type="button"
              onClick={() => legs.onCreate("traveler", TRAVELER_SUGGESTION)}
              className="rounded-md bg-blue-600 px-3 py-1.5 text-[13px] font-semibold text-white hover:opacity-90"
            >
              {line("wiringTravelerAccept", { type: TRAVELER_SUGGESTION })}
            </button>
            <button
              type="button"
              onClick={() => legs.onKeep("traveler")}
              className="rounded-md px-3 py-1.5 text-[13px] text-slate-700 hover:bg-slate-100"
            >
              {line("wiringTravelerKeep", { type: legs.circuitType })}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
